import React, { useState } from 'react';
import { ScenarioResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import ScenarioSimulation from '../simulations/ScenarioSimulation';
import { modelStories } from '../../data/stories';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';

interface ScenarioModuleProps {
  data: ScenarioResult | null;
  onOptimizationComplete: (data: ScenarioResult) => void;
  onLoadingStart: () => void;
}

const ScenarioModule: React.FC<ScenarioModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleRun = async () => {
    onLoadingStart();
    const result = await api.runScenario();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Scenario Planning</h2>
        <p className="text-gray-600 mb-4">
          Quantify risk using Monte Carlo simulation with Latin Hypercube Sampling
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.scenario} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleRun} className="btn-primary">Run Simulation</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Scenarios Run</div>
              <div className="text-2xl font-bold text-blue-600 flex items-center">
                <Activity className="w-5 h-5 mr-1" />
                {data.scenarios.toLocaleString()}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Expected Value</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(data.expected)}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">VaR (95%)</div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(data.var_95)}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <div className="text-2xl font-bold text-green-600">{data.status}</div>
            </div>
          </div>

          {/* Risk Metrics Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Risk Analysis Summary</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interpretation</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Expected NPV</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">{formatCurrency(data.expected)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Average outcome across all scenarios</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Positive</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Best Case (95th %ile)</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">
                    <div className="flex items-center justify-end">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      {formatCurrency(data.best_case)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">Top 5% of outcomes</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Upside</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Worst Case (5th %ile)</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">
                    <div className="flex items-center justify-end">
                      <TrendingDown className="w-4 h-4 mr-1" />
                      {formatCurrency(data.worst_case)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bottom 5% of outcomes</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Downside</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 bg-yellow-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Value at Risk (95%)</td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-red-600">{formatCurrency(data.var_95)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">5% chance of losing more than this</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Monitor</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 bg-orange-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Conditional VaR (95%)</td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-red-700">{formatCurrency(data.cvar_95)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Average loss in worst 5% scenarios</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">High Risk</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Risk Management Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <ScenarioSimulation />
      )}
    </div>
  );
};

export default ScenarioModule;
